import { getAccessToken, getAppVersion, resolveApiBase } from '@/lib/api/client';
import { hasConsent } from '@/lib/consent/consentState';
import { syncTelemetryFlagFromUrl } from '@/lib/telemetry-flag';
/**
 * In-browser product-analytics SDK. Buffers events client-side and flushes them in small batches to
 * `POST /analytics/collect`. Deliberately ISOLATED from the normal api client: it uses its own fetch
 * so an analytics 401/error can NEVER trigger a login redirect or surface to the user (analytics is
 * strictly best-effort and must not affect UX). It also never sends PHI — only event names + the
 * caller-supplied non-PHI props (the server sanitizes again).
 *
 * Optional under the EU cookie rules, so nothing is queued, stored or sent without the visitor's
 * analytics consent (lib/consent/consentState.ts; always granted where consent is not managed).
 */
const SID_KEY = 'nl_analytics_sid';
const MAX_BATCH = 20;
const FLUSH_MS = 5000;
let queue = [];
let timer = null;
let listenersBound = false;
/**
 * The clinic the signed-in user is currently acting in, stamped onto every event queued from
 * here on. Analytics could previously say WHAT happened and to which user, but not which
 * clinic's data it happened to — so "is this slow for one clinic or for everyone?" had no
 * answer, which is the first question any performance report raises.
 *
 * The server treats it as a HINT: it validates the caller's membership before recording it,
 * and tolerates its absence entirely, so a stale or missing value here is never a
 * correctness problem. Left null wherever the client genuinely doesn't know (a
 * multi-clinic user who hasn't opened anything clinic-scoped yet).
 */
let activeClinicId = null;
/** Set (or clear) the clinic stamped on subsequent events. Safe to call repeatedly. */
export function setAnalyticsClinic(clinicId) {
    activeClinicId = clinicId ?? null;
}
function sessionId() {
    try {
        let id = sessionStorage.getItem(SID_KEY);
        if (!id) {
            id = crypto.randomUUID();
            sessionStorage.setItem(SID_KEY, id);
        }
        return id;
    }
    catch {
        return '00000000-0000-0000-0000-000000000000';
    }
}
/** Queue one event. Non-blocking; safe to call from anywhere. */
export function track(event, properties, opts) {
    if (!event || !hasConsent('analytics'))
        return;
    queue.push({
        event,
        category: opts?.category,
        surface: opts?.surface,
        occurredAt: new Date().toISOString(),
        clinicId: activeClinicId ?? undefined,
        properties,
    });
    if (queue.length >= MAX_BATCH)
        flush();
    else
        scheduleFlush();
}
function scheduleFlush() {
    if (timer == null)
        timer = setTimeout(() => flush(), FLUSH_MS);
}
/** Send whatever is queued. `beacon` uses fetch keepalive so it survives page unload. */
export function flush(beacon = false) {
    if (timer != null) {
        clearTimeout(timer);
        timer = null;
    }
    if (queue.length === 0)
        return;
    // Consent withdrawn since these were queued.
    if (!hasConsent('analytics')) {
        queue = [];
        return;
    }
    const token = getAccessToken();
    if (!token) {
        // Not authenticated — drop silently (collect requires auth). Avoid unbounded growth.
        queue = [];
        return;
    }
    const events = queue;
    queue = [];
    const batch = {
        app: 'app-ui',
        appVersion: getAppVersion(),
        sessionId: sessionId(),
        events,
    };
    try {
        // Own fetch (NOT apiFetch) so a 401/error never calls handleUnauthorized / redirects.
        void fetch(`${resolveApiBase('/analytics/collect')}/analytics/collect`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(batch),
            keepalive: beacon,
        }).catch(() => {
            /* best-effort; intentionally swallowed */
        });
    }
    catch {
        /* never throw from analytics */
    }
}
/** Analytics consent withdrawn: drop anything queued and the per-tab session id. */
export function discardAnalytics() {
    if (timer != null) {
        clearTimeout(timer);
        timer = null;
    }
    queue = [];
    try {
        sessionStorage.removeItem(SID_KEY);
    }
    catch {
        // Storage unavailable: nothing was kept there either.
    }
}
/**
 * Replace high-cardinality / potentially-identifying path segments (uuids, long numbers) with :id,
 * and bearer tokens (report-download `rpt_�`, share `shr_�`, 43-char reset/invite tokens) with :token.
 */
export function sanitizePath(path) {
    return (path || '/')
        .split('?')[0]
        .split('#')[0]
        .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':id')
        .replace(/\/\d{2,}(?=\/|$)/g, '/:id')
        .replace(/\/(?:rpt_[A-Za-z0-9_-]{8,}|shr_[A-Za-z0-9]{8,}|[A-Za-z0-9_-]{43}(?![A-Za-z0-9_-]))(?=\/|$)/g, '/:token');
}
/** Surface = first non-empty (sanitized) path segment, e.g. "viewer", "teleconsultation". */
export function surfaceFromPath(path) {
    const seg = sanitizePath(path).split('/').filter(Boolean)[0];
    return seg ?? 'home';
}
/** Track a page/route view (auto-called by AnalyticsBridge on navigation). */
export function trackPageView(path) {
    track('nav.page_view', { path: sanitizePath(path) }, { category: 'nav', surface: surfaceFromPath(path) });
}
/** Convenience for feature events: trackFeature('viewer.tool_used', { tool: 'length' }). */
export function trackFeature(event, properties, surface) {
    track(event, properties, { surface });
}
/**
 * Bind unload listeners once so queued events aren't lost on navigation/close, and arm the
 * viewer's console-telemetry flag from the URL. Idempotent.
 *
 * The flag sync lives here because this module is EAGER and runs at app boot: `?telemetry=true`
 * used to be read only by `lib/telemetry.ts`, which ships in the lazy viewer chunk, so the
 * param did nothing unless it was typed directly onto a `/viewer/<id>` URL. Note this calls a
 * ~5-line helper rather than importing the telemetry module, which would pull the viewer
 * chunk into the initial bundle.
 */
export function startAnalytics() {
    syncTelemetryFlagFromUrl(typeof window === 'undefined' ? '' : window.location.search);
    if (listenersBound || typeof document === 'undefined')
        return;
    listenersBound = true;
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden')
            flush(true);
    });
    window.addEventListener('pagehide', () => flush(true));
}
